/**
 * ClientStatsStrip — Three-cell stats strip: Member Since · Analyses · Tier.
 *
 * Cells separated by 1px #E5E7EB dividers.
 * Third cell renders TierBadge instead of plain text.
 * Each cell is accessible with its own label.
 */

import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import type { TFunction } from '@dramadetector/shared';
import { GlassCard } from '../../glass/GlassCard';
import { TierBadge } from './TierBadge';

type Tier = 'free' | 'drama_plus' | 'dramax';

interface ClientStatsStripProps {
  memberSince: string;
  analysisCount: number;
  tier: Tier;
  t: TFunction<'practitioner'>;
}

const DIVIDER_STYLE = { width: 1, height: 32, backgroundColor: '#E5E7EB' } as const;

export const ClientStatsStrip: React.FC<ClientStatsStripProps> = ({
  memberSince,
  analysisCount,
  tier,
  t,
}) => (
  <GlassCard withChromeBorder={false} noPadding={false}>
    <View style={styles.row}>
      <View
        style={styles.cell}
        accessible={true}
        accessibilityLabel={`${t('stats.member_since')}: ${memberSince}`}
      >
        <Text style={styles.label}>{t('stats.member_since')}</Text>
        <Text style={styles.value}>{memberSince}</Text>
      </View>

      <View style={DIVIDER_STYLE} />

      <View
        style={styles.cell}
        accessible={true}
        accessibilityLabel={`${t('stats.analyses')}: ${analysisCount}`}
      >
        <Text style={styles.label}>{t('stats.analyses')}</Text>
        <Text style={styles.value}>{analysisCount}</Text>
      </View>

      <View style={DIVIDER_STYLE} />

      <View
        style={styles.cell}
        accessible={true}
        accessibilityLabel={`${t('stats.tier')}: ${t(('tier.' + tier) as never)}`}
      >
        <Text style={styles.label}>{t('stats.tier')}</Text>
        <TierBadge tier={tier} t={t} />
      </View>
    </View>
  </GlassCard>
);

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as ViewStyle,
  cell: {
    flex: 1,
    alignItems: 'center',
    gap: 4,
  },
  label: {
    fontSize: 11,
    fontWeight: '500',
    color: '#A1A1AA',
  },
  value: {
    fontSize: 14,
    fontWeight: '700',
    color: '#374151',
  },
});
